import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Pagination,
  Form,
  FormControl,
  Dropdown,
  Modal,
  Button,
  Navbar,
  Alert,
  Spinner,
} from "react-bootstrap";
import { ProjectContext } from "../providers/ProjectContext";
import { useAuth } from "../providers/AuthContext";
import AssetForm from "./AssetForm";
import {
  fetchAssetsByProject,
  fetchAllAssetsIncludingDeleted,
  verifyProjectAgainstSonder,
  repairProjectFromSonder,
  apiErrorMessage,
} from "../services/apiService";

// Human labels for the Sonder-sourced fields shown in the verify/repair modal.
const SONDER_FIELD_LABELS = {
  code: "Code",
  name: "Name",
  description: "Description",
  private: "Private",
  archived: "Archived",
  parent: "Parent folder",
  client: "Client",
};

const displaySonderValue = (field, value) => {
  if (field === "private" || field === "archived") return value ? "Yes" : "No";
  if (value === "" || value === null || value === undefined) return "(empty)";
  return String(value);
};

function AssetsList() {
  const navigate = useNavigate();

  const {
    currentProject,
    setCurrentProject,
    assetList,
    setCurrentAsset,
    refreshProjects,
  } = useContext(ProjectContext);
  const { currentUser } = useAuth();

  // Mirrors the backend requireAssetManage gate; the backend enforces it
  // regardless, this only hides controls that would 403.
  const canManage =
    currentUser &&
    (currentUser.role === "admin" || currentUser.canManageAssets);

  // Sonder verify/repair is strictly admin-only (matches requireAdmin on the
  // server). The backend enforces it regardless; this only hides the controls.
  const isAdmin = currentUser?.role === "admin";

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [assetsPerPage, setAssetsPerPage] = useState(10);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  // Sonderplan verify/repair state.
  const [sonderVerify, setSonderVerify] = useState(null);
  const [showSonderModal, setShowSonderModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState(() => new Set());
  const [repairing, setRepairing] = useState(false);
  const [repairError, setRepairError] = useState(null);

  const [assets, setAssets] = useState([]); // Add this line

  const handleAssetClick = (asset) => {
    setCurrentAsset(asset);
    navigate("/asset/" + asset._id);
  };

  const handleNewAsset = () => {
    setIsFormModalOpen(true);
  };

  const backToProjects = () => {
    setCurrentProject(null);
    navigate(currentProject ? "/projects-list" : "/assets-list", {
      replace: true,
    });
  };

  // Auto-verify against Sonderplan when an admin opens a project. One Sonder
  // call per open; failures degrade silently so the page always loads.
  useEffect(() => {
    if (!isAdmin || !currentProject?._id) {
      setSonderVerify(null);
      return;
    }
    let cancelled = false;
    verifyProjectAgainstSonder(currentProject._id)
      .then((data) => {
        if (!cancelled) setSonderVerify(data);
      })
      .catch(() => {
        if (!cancelled) setSonderVerify(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentProject?._id, isAdmin]);

  const openSonderModal = () => {
    setRepairError(null);
    setSelectedFields(new Set((sonderVerify?.diffs || []).map((d) => d.field)));
    setShowSonderModal(true);
  };

  const toggleSonderField = (field) =>
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) next.delete(field);
      else next.add(field);
      return next;
    });

  const handleApplyRepair = async () => {
    if (selectedFields.size === 0) return;
    setRepairing(true);
    setRepairError(null);
    try {
      const res = await repairProjectFromSonder(currentProject._id, [
        ...selectedFields,
      ]);
      if (res?.data) setCurrentProject(res.data);
      if (refreshProjects) await refreshProjects();
      setShowSonderModal(false);
      // Re-verify to clear the warning once the update has landed.
      const fresh = await verifyProjectAgainstSonder(currentProject._id).catch(
        () => null,
      );
      setSonderVerify(fresh);
    } catch (err) {
      setRepairError(apiErrorMessage(err, "Repair failed."));
    } finally {
      setRepairing(false);
    }
  };

  useEffect(() => {
    if (currentProject && currentProject._id) {
      fetchAssetsByProject(currentProject._id)
        .then((assets) => {
          setAssets(assets);
        })
        .catch((error) => {
          console.error(
            `Failed to fetch assets for project ${currentProject._id}`,
            error
          );
        });
    } else if (showDeleted && canManage) {
      // Manager-only view of soft-deleted assets (restorable from AssetView).
      fetchAllAssetsIncludingDeleted()
        .then((all) => {
          setAssets(all.filter((asset) => asset.isDeleted));
        })
        .catch((error) => {
          console.error("Failed to fetch deleted assets", error);
          setAssets([]);
        });
    } else {
      setAssets(assetList);
    }
  }, [currentProject, assetList, showDeleted, canManage]);

  // Filtering assets based on search term
  const filteredAssets = assets.filter((asset) => {
    const searchTerms = searchTerm.toLowerCase().split(",");

    return searchTerms.some((term) => {
      term = term.trim(); // Remove leading and trailing spaces
      return (
        asset.title.toLowerCase().includes(term) ||
        asset.keyNumber.toLowerCase().includes(term) ||
        asset.vandalId.toLowerCase().includes(term) ||
        asset.project.name.toLowerCase().includes(term) ||
        asset.project.code.toLowerCase().includes(term) ||
        asset.category.toLowerCase().includes(term) ||
        asset.producer.toLowerCase().includes(term)
      );
    });
  });

  // Pagination logic
  const indexOfLastAsset = currentPage * assetsPerPage;
  const indexOfFirstAsset = indexOfLastAsset - assetsPerPage;
  const currentAssets = filteredAssets.slice(
    indexOfFirstAsset,
    indexOfLastAsset
  );

  const totalPages = Math.ceil(filteredAssets.length / assetsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleAssetsPerPageChange = (e) => {
    setAssetsPerPage(Number(e));
    setCurrentPage(1); // Reset to first page with new page size
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Render pagination with limited visibility
  const renderPagination = () => {
    const paginationItems = [];
    for (let number = 1; number <= totalPages; number++) {
      if (
        number === 1 || // First page
        number === totalPages || // Last page
        (number >= currentPage - 1 && number <= currentPage + 1) // Nearby pages
      ) {
        paginationItems.push(
          <Pagination.Item
            key={number}
            active={number === currentPage}
            onClick={() => paginate(number)}
          >
            {number}
          </Pagination.Item>
        );
      } else if (
        number === currentPage - 2 || // Ellipsis before current page
        number === currentPage + 2 // Ellipsis after current page
      ) {
        paginationItems.push(
          <Pagination.Ellipsis key={`ellipsis-${number}`} disabled />
        );
      }
    }

    return (
      <Pagination className="justify-content-start mb-0">
        {paginationItems}
        <Pagination.First onClick={() => paginate(1)} />
        <Pagination.Prev onClick={() => paginate(currentPage - 1)} />
        <Pagination.Next onClick={() => paginate(currentPage + 1)} />
        <Pagination.Last onClick={() => paginate(totalPages)} />
      </Pagination>
    );
  };

  return (
    <div className="bg-white shadow-md rounded p-6">
      <Navbar className="shadow-sm w-full bg-white" expand="lg" fixed="top">
        <div className="pb-1 pt-20 w-full px-6">
          <div className="flex flex-row justify-between">
            {currentProject ? (
              <div className="flex flex-row items-center gap-4">
                <Button onClick={backToProjects} className="mb-4">
                  Back
                </Button>
                <h2 className="text-2xl font-bold mb-4">
                  {currentProject.code ? currentProject.code : "#####"} -{" "}
                  {currentProject.name}
                </h2>
                {isAdmin && (
                  <div className="flex flex-row items-center gap-3 mb-4">
                    {(sonderVerify?.sonderLink || currentProject.sonderId) && (
                      <a
                        href={
                          sonderVerify?.sonderLink ||
                          `https://vandal.sonderplan.com/project/edit?id=${currentProject.sonderId}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm whitespace-nowrap"
                      >
                        Sonder ↗
                      </a>
                    )}
                    {sonderVerify?.diffs?.length > 0 && (
                      <Button
                        size="sm"
                        variant="warning"
                        onClick={openSonderModal}
                        className="whitespace-nowrap"
                      >
                        ⚠ {sonderVerify.diffs.length} out of sync
                      </Button>
                    )}
                    {sonderVerify?.reason === "sonder-not-found" && (
                      <span
                        className="text-sm text-orange-600 whitespace-nowrap"
                        title="The stored Sonder id no longer resolves in Sonderplan"
                      >
                        ⚠ Not found in Sonder
                      </span>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <h2 className="text-2xl font-bold mb-4">
                {showDeleted ? "Deleted Assets" : "All Assets"}
              </h2>
            )}
            <div className="flex flex-row gap-3 items-center">
              {canManage && !currentProject && (
                <Form.Check
                  type="switch"
                  id="show-deleted-switch"
                  label="Show deleted"
                  checked={showDeleted}
                  onChange={(e) => {
                    setShowDeleted(e.target.checked);
                    setCurrentPage(1);
                  }}
                />
              )}
              <Button onClick={handleNewAsset} className="mb-4">
                Add New Asset
              </Button>
            </div>
          </div>
          <div className="flex flex-row justify-between items-center mb-4">
            <div className="flex justify-between items-center w-full">
              <Form className="w-[80%]">
                <FormControl
                  type="text"
                  placeholder="Search Assets"
                  className="mr-sm-2"
                  onChange={handleSearchTermChange}
                />
              </Form>
            </div>
            <div className="flex flex-row gap-3 align-middle items-center">
              {renderPagination()}
              <Dropdown>
                <Dropdown.Toggle id="dropdown-basic">
                  {assetsPerPage}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {[10, 20, 30, 40, 50].map((number) => (
                    <Dropdown.Item
                      as="button"
                      key={number}
                      onClick={() => handleAssetsPerPageChange(number)}
                    >
                      {number}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              <div>Per Page</div>
            </div>
          </div>
        </div>
      </Navbar>
      <div className="px-6 pb-6 mt-60">
        {currentAssets.length > 0 ? (
          <ul className="list-none">
            {currentAssets.map((asset, index) => (
              <li
                key={index}
                className="cursor-pointer p-2 hover:bg-gray-200"
                onClick={() => handleAssetClick(asset)}
              >
                {asset.isDeleted && (
                  <div className="text-red-600 font-bold">DELETED</div>
                )}
                {asset.category && asset.category.trim() !== "" && (
                  <div>
                    <strong>Category:</strong> {asset.category}
                  </div>
                )}
                {asset.title && asset.title.trim() !== "" && (
                  <div>
                    <strong>Title:</strong> {asset.title}
                  </div>
                )}
                {asset.vandalId && asset.vandalId.trim() !== "" && (
                  <div>
                    <strong>Vandal ID:</strong> {asset.vandalId}
                  </div>
                )}
                {asset.keyNumber && asset.keyNumber.trim() !== "" && (
                  <div>
                    <strong>Key Number:</strong> {asset.keyNumber}
                  </div>
                )}
                {asset.project &&
                  asset.project.name &&
                  asset.project.name.trim() !== "" && (
                    <div>
                      <strong>Project:</strong>{" "}
                      {asset.project.code && asset.project.code.trim() !== ""
                        ? asset.project.code
                        : "#####"}{" "}
                      - {asset.project.name}
                    </div>
                  )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="italic">There are no assets.</div>
        )}
      </div>
      {isFormModalOpen && (
        <Modal
          size="lg"
          show={isFormModalOpen}
          onHide={() => setIsFormModalOpen(false)}
        >
          <Modal.Header closeButton>
            <Modal.Title>{"Add Asset"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AssetForm
              asset={null}
              closeFormModal={() => setIsFormModalOpen(false)}
            />
          </Modal.Body>
        </Modal>
      )}

      <Modal
        show={showSonderModal}
        onHide={() => !repairing && setShowSonderModal(false)}
        centered
      >
        <Modal.Header closeButton={!repairing}>
          <Modal.Title>Sync project from Sonderplan</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-600">
            These fields differ between Sonderplan and Tracer. Tick the ones to
            update in Tracer. Applying <strong>Code</strong> or{" "}
            <strong>Name</strong> also renames the Xero and Toggl projects;
            applying <strong>Client</strong> re-points the Xero and Toggl
            projects at that client.
          </p>
          {(sonderVerify?.diffs || []).map((d) => (
            <div key={d.field} className="border rounded p-2 mb-2">
              <Form.Check
                type="checkbox"
                id={`sonder-field-${d.field}`}
                checked={selectedFields.has(d.field)}
                onChange={() => toggleSonderField(d.field)}
                label={
                  <span className="font-semibold">
                    {SONDER_FIELD_LABELS[d.field] || d.field}
                  </span>
                }
              />
              <div className="text-sm ml-4 mt-1">
                <div>
                  <span className="text-gray-500">Sonder:</span>{" "}
                  {displaySonderValue(d.field, d.sonderValue)}
                </div>
                <div>
                  <span className="text-gray-500">Tracer:</span>{" "}
                  {displaySonderValue(d.field, d.tracerValue)}
                </div>
              </div>
            </div>
          ))}
          {repairError && (
            <Alert variant="danger" className="mb-0 mt-2">
              {repairError}
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowSonderModal(false)}
            disabled={repairing}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyRepair}
            disabled={repairing || selectedFields.size === 0}
          >
            {repairing ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Applying…
              </>
            ) : (
              `Apply selected (${selectedFields.size})`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AssetsList;
